import { check } from "~/utils/quizzes";
import { QuizQuestionType } from "~/constants";

import type { QuizQuestionType as GqlQuizQuestionType } from "~/graphql/types";
import type { FillInTheBlankQuestionProps } from "../fillInTheBlankQuestion/FillInTheBlankQuestion";
import type { MatchingQuestionProps } from "../matchingQuestion/MatchingQuestion";
import type { MultipleChoiceQuestionProps } from "../multipleChoiceQuestion/MultipleChoiceQuestion";
import type { OrderingQuestionProps } from "../orderingQuestion/OrderingQuestion";
import type { SingleChoiceQuestionProps } from "../singleChoiceQuestion/SingleChoiceQuestion";

export type QuizViewQuestionProps =
  | FillInTheBlankQuestionProps
  | MatchingQuestionProps
  | MultipleChoiceQuestionProps
  | OrderingQuestionProps
  | SingleChoiceQuestionProps;

export interface QuizViewQuestionValidityState {
  invalidCount: Maybe<number>;
  valid: boolean;
  validCount: Maybe<number>;
}

export interface QuizViewQuestionFeedbackMessage {
  description?: Maybe<string>;
  title?: Maybe<string>;
}

export interface QuizViewQuestion {
  /**
   * The correct answer
   */
  answer?: QuizViewQuestionProps["answer"];
  /**
   * Validity check override
   */
  check?: (question: QuizViewQuestion) => QuizViewQuestionValidityState;
  /**
   * A description of the question's content
   */
  description?: string;
  /**
   * Unique ID for the question
   */
  id: string;
  /**
   * Whether or not the user has completed the question
   */
  isCompleted?: boolean;
  /**
   * Whether or not to display validity
   */
  isValidityVisible?: boolean;
  /**
   * (Matching only) The separate parts of a multi-part
   * question
   */
  items?: MatchingQuestionProps["items"];
  /**
   * A list of potential answer choices
   */
  options?: QuizViewQuestionProps["options"];
  /**
   * A message to display when the user answers correctly
   */
  successMessage?: QuizViewQuestionFeedbackMessage;
  /**
   * A message to display when the user answers incorrectly
   */
  errorMessage?: QuizViewQuestionFeedbackMessage;
  /**
   * (Fill-in-the-blank only) Text used to build the
   * question prompt
   */
  template?: FillInTheBlankQuestionProps["template"];
  /**
   * Title for the question
   */
  title: string;
  /**
   * Determines which underlying component to render
   */
  type: QuizQuestionType | GqlQuizQuestionType;
  /**
   * The user's answer to the question
   */
  value?: QuizViewQuestionProps["answer"];
  /**
   * Validity state of the question
   */
  validity?: QuizViewQuestionValidityState;
}

export interface QuizViewManagerInit {
  defaultQuestionIndex?: number;
  questions?: QuizViewQuestion[];
  onGoBack?: () => void;
  onGoForward?: () => void;
  onInvalid?: (question: QuizViewQuestion) => void;
  onUpdate?: () => void;
  onValid?: (question: QuizViewQuestion) => void;
}

/**
 * Manages the state of a QuizView
 * @class
 */
export class QuizViewManager {
  currentQuestionIndex: number;
  questions: QuizViewQuestion[];

  onGoBack: QuizViewManagerInit["onGoBack"];
  onGoForward: QuizViewManagerInit["onGoForward"];
  onInvalid: QuizViewManagerInit["onInvalid"];
  onUpdate: QuizViewManagerInit["onUpdate"];
  onValid: QuizViewManagerInit["onValid"];

  constructor(init?: QuizViewManagerInit) {
    this.currentQuestionIndex = init?.defaultQuestionIndex ?? 0;
    this.questions = init?.questions ?? [];

    this.onGoBack = init?.onGoBack;
    this.onGoForward = init?.onGoForward;
    this.onInvalid = init?.onInvalid;
    this.onValid = init?.onValid;
    this.onUpdate = init?.onUpdate;
  }

  get questionCount() {
    return this.questions.length;
  }

  get currentQuestion() {
    return this.questions[this.currentQuestionIndex];
  }

  get hasPreviousQuestion() {
    return this.currentQuestionIndex > 0;
  }

  get hasNextQuestion() {
    return this.currentQuestionIndex < this.questionCount - 1;
  }

  /**
   * Signals a change in state. Used to trigger a re-render
   */
  update() {
    this.onUpdate?.();
  }

  /**
   * Resets the quiz to an initial state
   */
  reset(
    init?: Pick<QuizViewManagerInit, "defaultQuestionIndex" | "questions">,
  ) {
    if (init?.questions) {
      this.questions = init.questions;
    }

    this.currentQuestionIndex = init?.defaultQuestionIndex ?? 0;

    this.update();
  }

  /**
   * Updates the properties of the specified question
   */
  updateQuestion(
    id: string,
    updater: (question: QuizViewQuestion) => QuizViewQuestion,
  ) {
    this.questions = this.questions.map((q) => {
      if (q.id === id) {
        return updater(q);
      }

      return q;
    });

    return this.questions.find((q) => q.id === id);
  }

  /**
   * Resets a specific question
   */
  resetQuestion(id?: string) {
    this.questions = this.questions.map((q, i) => {
      const shouldReset =
        // A specific question
        (id && q.id === id) ||
        // The current question
        (!id && i === this.currentQuestionIndex);

      if (shouldReset) {
        return {
          ...q,
          isCompleted: false,
          isValidityVisible: false,
          validity: undefined,
          value: undefined,
        };
      }

      return q;
    });

    this.update();
  }

  /**
   * Sets the user's response as the `value` of the
   * specified question
   */
  setQuestionValue(id: string, value: string | string[]) {
    this.updateQuestion(id, (q) => ({
      ...q,
      validity: undefined,
      value,
    }));

    this.update();
  }

  /**
   * Sets the visibility of the specified question's
   * validity data
   */
  setValidityVisible(id: string, isValidityVisible: boolean = true) {
    this.updateQuestion(id, (q) => ({
      ...q,
      isValidityVisible,
    }));
  }

  /**
   * Marks the specified question as completed
   */
  setQuestionCompleted(id: string, isCompleted: boolean = true) {
    this.updateQuestion(id, (q) => ({
      ...q,
      isCompleted,
    }));
  }

  /**
   * Checks the validity of the specified question
   */
  checkValidity(id: string) {
    const question = this.updateQuestion(id, (q) => ({
      ...q,
      validity:
        q.check?.(q) ??
        check(q.answer, q.value, {
          order: q.type === QuizQuestionType.Ordering,
        }),
    }));

    if (question?.validity?.valid === true) {
      this.onValid?.(question);
    }

    if (question?.validity?.valid === false) {
      this.onInvalid?.(question);
    }

    return question?.validity;
  }

  /**
   * Checks validity of the specified question and marks the
   * results as visible
   */
  reportValidity(id: string) {
    const validity = this.checkValidity(id);
    this.setValidityVisible(id);
    this.update();
    return validity;
  }

  /**
   * Goes to the previous question
   */
  goBack() {
    this.currentQuestionIndex = Math.max(0, this.currentQuestionIndex - 1);
    this.onGoBack?.();
    this.update();
  }

  /**
   * Goes to the next question
   */
  goForward() {
    this.currentQuestionIndex = Math.min(
      this.questions.length - 1,
      this.currentQuestionIndex + 1,
    );
    this.onGoForward?.();
    this.update();
  }
}
